export type PendingEditableCellEditorKind = 'text' | 'checkbox' | 'listbox'

export type PendingEditableCellCommit = {
  fieldName: string
  rowIndex: number
  newValue: unknown
  editorKind: PendingEditableCellEditorKind
}

const editableCellDrafts = new Map<string, PendingEditableCellCommit>()

export function rememberEditableCellDraft(
  tableKey: string,
  pending: PendingEditableCellCommit,
): void {
  const key = tableKey.trim()
  if (!key || !pending.fieldName || pending.rowIndex < 0) return
  editableCellDrafts.set(key, pending)
}

export function peekEditableCellDraft(tableKey: string): PendingEditableCellCommit | null {
  return editableCellDrafts.get(tableKey.trim()) ?? null
}

export function clearEditableCellDraft(tableKey: string): void {
  editableCellDrafts.delete(tableKey.trim())
}

export function readListboxEditorPendingValue(rawValue: string): unknown {
  if (rawValue === 'true') return true
  if (rawValue === 'false') return false
  if (rawValue !== '' && /^-?\d+$/.test(rawValue)) return Number(rawValue)
  return rawValue
}

type ElementLike = {
  nodeType?: number
  classList?: { contains: (name: string) => boolean }
  closest?: (selector: string) => ElementLike | null
  contains?: (node: unknown) => boolean
  querySelector?: (selector: string) => ElementLike | null
  querySelectorAll?: (selector: string) => ArrayLike<ElementLike>
  getAttribute?: (name: string) => string | null
  value?: string
  checked?: boolean
  dataset?: { selectedValue?: string }
}

function isElementLike(value: unknown): value is ElementLike {
  if (value == null || typeof value !== 'object') return false
  if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) return true
  return (value as ElementLike).nodeType === 1
}

function hasClass(element: ElementLike, className: string): boolean {
  return Boolean(element.classList?.contains(className))
}

function closestEditor(element: ElementLike): ElementLike | null {
  if (
    hasClass(element, 'table-cell-editor') ||
    hasClass(element, 'table-cell-editor-checkbox') ||
    hasClass(element, 'table-cell-editor-listbox')
  ) {
    return element
  }
  const found = element.closest?.(
    '.table-cell-editor, .table-cell-editor-checkbox, .table-cell-editor-listbox',
  )
  return found && isElementLike(found) ? found : null
}

function findOpenCellEditor(tableRoot: ElementLike): ElementLike | null {
  const cells = tableRoot.querySelectorAll?.('td[data-p-cell-editing]')
  if (!cells) return null
  for (const cell of Array.from(cells)) {
    if (!isElementLike(cell)) continue
    const flag = cell.getAttribute?.('data-p-cell-editing')
    if (flag === 'false') continue
    const editor = cell.querySelector?.(
      '.table-cell-editor, .table-cell-editor-checkbox, .table-cell-editor-listbox',
    )
    if (isElementLike(editor)) return editor
  }
  return null
}

function readEditorCommit(
  editor: ElementLike,
  options: {
    itemsLength: number
    resolveColumnName: (target: HTMLElement | null | undefined) => string
  },
): PendingEditableCellCommit | null {
  const listboxWrapper = hasClass(editor, 'table-cell-editor-listbox')
    ? editor
    : editor.closest?.('.table-cell-editor-listbox')
  if (listboxWrapper && isElementLike(listboxWrapper)) {
    const row = listboxWrapper.closest?.('tr')
    const tbody = row && isElementLike(row) ? row.closest?.('tbody') : null
    if (!row || !isElementLike(row) || !tbody || !isElementLike(tbody)) return null
    const rowIndex = Array.from(tbody.querySelectorAll?.('tr') ?? []).indexOf(row)
    if (rowIndex < 0 || rowIndex >= options.itemsLength) return null
    const fieldName = options.resolveColumnName(listboxWrapper as HTMLElement)
    if (!fieldName) return null
    const hidden = listboxWrapper.querySelector?.('.table-cell-editor-listbox-value')
    const rawValue = hidden?.value ?? listboxWrapper.dataset?.selectedValue ?? ''
    return {
      fieldName,
      rowIndex,
      newValue: readListboxEditorPendingValue(rawValue),
      editorKind: 'listbox',
    }
  }

  const isTextEditor = hasClass(editor, 'table-cell-editor')
  const isCheckboxEditor = hasClass(editor, 'table-cell-editor-checkbox')
  if (!isTextEditor && !isCheckboxEditor) return null

  const row = editor.closest?.('tr')
  const tbody = row && isElementLike(row) ? row.closest?.('tbody') : null
  if (!row || !isElementLike(row) || !tbody || !isElementLike(tbody)) return null
  const rowIndex = Array.from(tbody.querySelectorAll?.('tr') ?? []).indexOf(row)
  if (rowIndex < 0 || rowIndex >= options.itemsLength) return null
  const fieldName = options.resolveColumnName(editor as HTMLElement)
  if (!fieldName) return null

  return {
    fieldName,
    rowIndex,
    newValue: isCheckboxEditor ? Boolean(editor.checked) : (editor.value ?? ''),
    editorKind: isCheckboxEditor ? 'checkbox' : 'text',
  }
}

export function resolvePendingEditableCellCommit(
  tableRoot: HTMLElement | null,
  options: {
    cellEditable: boolean
    itemsLength: number
    resolveColumnName: (target: HTMLElement | null | undefined) => string
  },
): PendingEditableCellCommit | null {
  if (!options.cellEditable || !tableRoot || !isElementLike(tableRoot)) return null

  const active =
    typeof document !== 'undefined' && isElementLike(document.activeElement)
      ? document.activeElement
      : null
  const focusedEditor =
    active && tableRoot.contains?.(active as Node) ? closestEditor(active) : null
  const editor = focusedEditor ?? findOpenCellEditor(tableRoot)
  if (!editor) return null
  return readEditorCommit(editor, options)
}

export type EditableTableCommitHook = () => boolean | Promise<boolean>

export async function flushPendingEditableTableEditsFromRegistry(
  elements: Record<string, { formId?: string | number; commitPendingCellEdit?: EditableTableCommitHook }>,
  formId?: string | number,
  tick: () => Promise<void> = async () => {},
): Promise<boolean> {
  const wanted =
    formId != null && formId !== '' && formId !== -1 ? String(formId) : null
  let committed = false

  for (const element of Object.values(elements)) {
    if (element == null) continue
    if (wanted && String(element.formId ?? '') !== wanted) continue
    const hook = element.commitPendingCellEdit
    if (typeof hook !== 'function') continue
    const result = hook()
    const didCommit = result instanceof Promise ? await result : result
    if (didCommit) committed = true
  }

  if (committed) {
    await tick()
    await tick()
  }

  return committed
}
